import { LogIn, TriangleAlert } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

import { BrandMark } from '@/components/BrandMark';
import { errorMessage, SESSION_TIMEOUT_MS, withTimeout } from '@/components/admin/adminError';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';
import { adminLogin, adminSession } from '@/lib/adminApi';

export function AdminLoginPage() {
  const navigate = useNavigate();
  const [checkingSession, setCheckingSession] = useState(true);
  const [alreadySignedIn, setAlreadySignedIn] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let ignore = false;

    withTimeout(adminSession(), SESSION_TIMEOUT_MS, 'The server did not respond.')
      .then(() => {
        if (!ignore) {
          setAlreadySignedIn(true);
          setCheckingSession(false);
        }
      })
      .catch(() => {
        if (!ignore) setCheckingSession(false);
      });

    return () => {
      ignore = true;
    };
  }, []);

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setPending(true);
    setError(null);

    try {
      await adminLogin(email.trim(), password);
      navigate('/admin', { replace: true });
    } catch (err) {
      // The server writes these for humans — a friendly line for bad
      // credentials and a "try again in N min" for the throttle — so they are
      // shown as-is rather than mapped to something vaguer.
      setError(errorMessage(err, 'Could not sign in. Try again.'));
      setPending(false);
    }
  };

  if (checkingSession) {
    return (
      <div className="grid min-h-full place-items-center p-6">
        <Spinner className="size-6" />
      </div>
    );
  }

  if (alreadySignedIn) {
    return <Navigate to="/admin" replace />;
  }

  return (
    <main className="grid min-h-full place-items-center p-4 sm:p-6">
      <div className="w-full max-w-sm">
        <BrandMark size="lg" className="mb-8 justify-center" />

        <Card className="animate-in fade-in-0 slide-in-from-bottom-3 p-6 duration-500 sm:p-8">
          <h1 className="text-center text-xl font-semibold tracking-tight text-balance">
            Admin console
          </h1>
          <p className="text-muted-foreground mt-2 text-center text-sm text-pretty">
            Sign in to review interviews, recordings and join links.
          </p>

          <form className="mt-7 space-y-4" onSubmit={onSubmit} noValidate>
            <div className="space-y-2">
              <Label htmlFor="admin-email">Email</Label>
              <Input
                id="admin-email"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                autoComplete="email"
                autoCapitalize="none"
                spellCheck={false}
                required
                disabled={pending}
                aria-invalid={error !== null}
                placeholder="you@company.com"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="admin-password">Password</Label>
              <Input
                id="admin-password"
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete="current-password"
                required
                disabled={pending}
                aria-invalid={error !== null}
              />
            </div>

            {error && (
              <p
                role="alert"
                className="flex items-start gap-2 rounded-xl border border-[color-mix(in_oklab,var(--destructive)_40%,transparent)] bg-[color-mix(in_oklab,var(--destructive)_14%,transparent)] px-3 py-2.5 text-sm text-[color-mix(in_oklab,var(--destructive)_80%,white)]"
              >
                <TriangleAlert className="mt-0.5 size-4 shrink-0" aria-hidden />
                <span className="text-pretty">{error}</span>
              </p>
            )}

            <Button
              type="submit"
              className="w-full"
              disabled={pending || !email.trim() || !password}
            >
              {pending ? <Spinner className="size-4" /> : <LogIn className="size-4" />}
              {pending ? 'Signing in…' : 'Sign in'}
            </Button>
          </form>
        </Card>

        <p className="text-muted-foreground mt-6 text-center text-xs text-pretty">
          This console is for Hiredude staff. Candidates and interviewers join from their invite
          link.
        </p>
      </div>
    </main>
  );
}
